import { API_ENDPOINTS, httpClientInstance } from "@/api"
import type {
  CostAlert,
  CostAlertBulkAction,
  CostAlertBulkActionResponse,
  CostAlertEventStatus,
  CostAlertHistoryItem,
  CostAlertHistoryResponse,
  CostAlertLimitInfo,
  CostAlertListResponse,
  CostAlertQuery,
  CostAlertStatistics,
  CostAlertStatisticsResponse,
  CreateCostAlertRequest,
  UpdateCostAlertRequest,
} from "@/types"
import { create } from "zustand"

export const COST_ALERT_PAGE_SIZE = 10

export const DEFAULT_COST_ALERT_QUERY: CostAlertQuery = {
  page: 1,
  pageSize: COST_ALERT_PAGE_SIZE,
  search: "",
  status: null,
  actions: [],
  risk: null,
  ordering: "server_name",
  descending: false,
}

/** Error thrown by the write actions: a readable message plus the API's per-field messages. */
export class CostAlertApiError extends Error {
  fieldErrors: Record<string, string[]>
  /** The API's machine-readable code (e.g. cost_alert_limit_reached), when it sent one */
  code?: string

  constructor(message: string, fieldErrors: Record<string, string[]> = {}, code?: string) {
    super(message)
    this.name = "CostAlertApiError"
    this.fieldErrors = fieldErrors
    this.code = code
  }
}

const toApiError = (error: unknown, fallback: string): CostAlertApiError => {
  const body = (
    error as {
      response?: { data?: { message?: string; detail?: string; code?: string; errors?: Record<string, string[]> } }
    }
  )?.response?.data
  const fieldErrors = body?.errors ?? {}
  const firstFieldMessage = Object.values(fieldErrors)[0]?.[0]
  const message = firstFieldMessage ?? body?.message ?? body?.detail ?? (error instanceof Error ? error.message : fallback)
  return new CostAlertApiError(message || fallback, fieldErrors, body?.code)
}

/** The list endpoint's query string for the table state. */
export const buildAlertParams = (query: CostAlertQuery) => {
  const params: Record<string, string | number> = { page: query.page, page_size: query.pageSize }
  if (query.search.trim()) params.search = query.search.trim()
  if (query.status) params.status = query.status
  if (query.actions.length > 0) params.actions = query.actions.join(",")
  if (query.risk) params.risk = query.risk
  params.ordering = `${query.descending ? "-" : ""}${query.ordering}`
  return params
}

interface CostAlertStore {
  // Alerts tab
  alerts: CostAlert[]
  alertsCount: number
  query: CostAlertQuery
  /** The owner's tier maximum and how many alerts they have (from the list response) */
  limitInfo: CostAlertLimitInfo | null
  isLoading: boolean
  error: string | null
  togglingIds: string[]

  // History tab
  alertHistory: CostAlertHistoryItem[]
  historyCount: number
  historyNext: string | null
  historyIsLoading: boolean
  historyError: string | null
  historyStatus: CostAlertEventStatus | null
  resolvingIds: string[]

  // Statistics tab (and the dashboard KPI)
  statistics: CostAlertStatistics | null
  statisticsIsLoading: boolean
  statisticsError: string | null

  fetchAlerts: (patch?: Partial<CostAlertQuery>) => Promise<void>
  fetchAlertForServer: (serverId: string) => Promise<CostAlert | null>
  createAlert: (payload: CreateCostAlertRequest) => Promise<CostAlert>
  updateAlert: (id: string, payload: UpdateCostAlertRequest) => Promise<CostAlert>
  deleteAlert: (id: string) => Promise<void>
  toggleAlertStatus: (id: string) => Promise<void>
  bulkAction: (ids: string[], action: CostAlertBulkAction) => Promise<CostAlertBulkActionResponse>
  fetchAlertHistory: () => Promise<void>
  loadMoreAlertHistory: () => Promise<void>
  setHistoryStatus: (status: CostAlertEventStatus | null) => Promise<void>
  fetchStatistics: () => Promise<void>
  resolveAlert: (id: string) => Promise<void>
  clearError: () => void
  reset: () => void
}

const initialState = {
  alerts: [],
  alertsCount: 0,
  query: DEFAULT_COST_ALERT_QUERY,
  limitInfo: null,
  isLoading: false,
  error: null,
  togglingIds: [],
  alertHistory: [],
  historyCount: 0,
  historyNext: null,
  historyIsLoading: false,
  historyError: null,
  historyStatus: null,
  resolvingIds: [],
  statistics: null,
  statisticsIsLoading: false,
  statisticsError: null,
}

export const useCostAlertStore = create<CostAlertStore>((set, get) => {
  // Each list keeps a request counter: a response that is no longer the latest request is
  // dropped, so a slow older request can never overwrite a newer one (fast typing, paging or
  // filter changes).
  let alertsRequest = 0
  let historyRequest = 0
  let statisticsRequest = 0

  /** After any write: refresh the visible table page and the statistics. */
  const refreshAfterWrite = async () => {
    await Promise.all([get().fetchAlerts(), get().fetchStatistics()])
  }

  const historyParams = (page?: string) => {
    const params: Record<string, string> = {}
    const { historyStatus } = get()
    if (historyStatus) params.status = historyStatus
    if (page) params.page = page
    return params
  }

  return {
    ...initialState,

    fetchAlerts: async (patch) => {
      const query = { ...get().query, ...patch }
      const requestId = ++alertsRequest
      set({ query, isLoading: true, error: null })
      try {
        const response = await httpClientInstance.get<CostAlertListResponse>(
          API_ENDPOINTS.costAlerts.root,
          buildAlertParams(query)
        )
        // A page past the end (e.g. after deleting the last row of it) falls back to the last page.
        if (requestId !== alertsRequest) return
        const lastPage = Math.max(1, Math.ceil(response.count / query.pageSize))
        if (query.page > lastPage) {
          await get().fetchAlerts({ page: lastPage })
          return
        }
        set({
          alerts: response.data,
          alertsCount: response.count,
          limitInfo: response.meta ?? get().limitInfo,
          isLoading: false,
        })
      } catch (error) {
        if (requestId !== alertsRequest) return
        set({ error: toApiError(error, "Failed to fetch cost alerts").message, isLoading: false })
      }
    },

    fetchAlertForServer: async (serverId) => {
      try {
        const response = await httpClientInstance.get<CostAlertListResponse>(API_ENDPOINTS.costAlerts.root, {
          server_id: serverId,
        })
        return response.data[0] ?? null
      } catch {
        return null
      }
    },

    createAlert: async (payload) => {
      try {
        const response = await httpClientInstance.post<{ data: CostAlert }>(API_ENDPOINTS.costAlerts.root, payload)
        await refreshAfterWrite()
        return response.data
      } catch (error) {
        throw toApiError(error, "Failed to create cost alert")
      }
    },

    updateAlert: async (id, payload) => {
      try {
        const response = await httpClientInstance.patch<{ data: CostAlert }>(
          API_ENDPOINTS.costAlerts.detail(id),
          payload
        )
        await refreshAfterWrite()
        return response.data
      } catch (error) {
        throw toApiError(error, "Failed to update cost alert")
      }
    },

    deleteAlert: async (id) => {
      try {
        await httpClientInstance.delete(API_ENDPOINTS.costAlerts.detail(id))
        await refreshAfterWrite()
      } catch (error) {
        throw toApiError(error, "Failed to delete cost alert")
      }
    },

    toggleAlertStatus: async (id) => {
      set((state) => ({ togglingIds: [...state.togglingIds, id], error: null }))
      try {
        await httpClientInstance.post(API_ENDPOINTS.costAlerts.toggleStatus(id))
        await refreshAfterWrite()
      } catch (error) {
        // The table still shows the previous state; surface the reason.
        set({ error: toApiError(error, "Failed to update alert status").message })
      } finally {
        set((state) => ({ togglingIds: state.togglingIds.filter((togglingId) => togglingId !== id) }))
      }
    },

    bulkAction: async (ids, action) => {
      try {
        const response = await httpClientInstance.post<CostAlertBulkActionResponse>(API_ENDPOINTS.costAlerts.bulkAction, {
          alert_ids: ids,
          action,
        })
        await refreshAfterWrite()
        return response
      } catch (error) {
        throw toApiError(error, "Failed to update the selected alerts")
      }
    },

    fetchAlertHistory: async () => {
      const requestId = ++historyRequest
      set({ historyIsLoading: true, historyError: null })
      try {
        const response = await httpClientInstance.get<CostAlertHistoryResponse>(
          API_ENDPOINTS.costAlerts.history,
          historyParams()
        )
        if (requestId !== historyRequest) return
        set({
          alertHistory: response.data,
          historyCount: response.count,
          historyNext: response.next,
          historyIsLoading: false,
        })
      } catch (error) {
        if (requestId !== historyRequest) return
        set({
          historyError: error instanceof Error ? error.message : "Failed to fetch alert history",
          historyIsLoading: false,
        })
      }
    },

    loadMoreAlertHistory: async () => {
      const { historyNext } = get()
      if (!historyNext) return
      const requestId = ++historyRequest
      set({ historyIsLoading: true, historyError: null })
      try {
        const page = new URL(historyNext).searchParams.get("page") ?? "2"
        const response = await httpClientInstance.get<CostAlertHistoryResponse>(
          API_ENDPOINTS.costAlerts.history,
          historyParams(page)
        )
        if (requestId !== historyRequest) return
        set((state) => ({
          alertHistory: [...state.alertHistory, ...response.data],
          historyCount: response.count,
          historyNext: response.next,
          historyIsLoading: false,
        }))
      } catch (error) {
        if (requestId !== historyRequest) return
        set({
          historyError: error instanceof Error ? error.message : "Failed to load more history",
          historyIsLoading: false,
        })
      }
    },

    setHistoryStatus: async (status) => {
      set({ historyStatus: status })
      await get().fetchAlertHistory()
    },

    fetchStatistics: async () => {
      const requestId = ++statisticsRequest
      set({ statisticsIsLoading: true, statisticsError: null })
      try {
        const response = await httpClientInstance.get<CostAlertStatisticsResponse>(API_ENDPOINTS.costAlerts.statistics)
        if (requestId !== statisticsRequest) return
        set({ statistics: response.data, statisticsIsLoading: false })
      } catch (error) {
        if (requestId !== statisticsRequest) return
        set({
          statisticsError: error instanceof Error ? error.message : "Failed to fetch cost alert statistics",
          statisticsIsLoading: false,
        })
      }
    },

    resolveAlert: async (id) => {
      set((state) => ({ resolvingIds: [...state.resolvingIds, id] }))
      try {
        const updated = await httpClientInstance.post<CostAlertHistoryItem>(API_ENDPOINTS.costAlerts.resolve(id))
        set((state) => ({
          alertHistory: state.alertHistory.map((item) => (item.id === id ? updated : item)),
          resolvingIds: state.resolvingIds.filter((resolvingId) => resolvingId !== id),
        }))
      } catch (error) {
        set((state) => ({
          historyError: error instanceof Error ? error.message : "Failed to resolve alert",
          resolvingIds: state.resolvingIds.filter((resolvingId) => resolvingId !== id),
        }))
      }
    },

    clearError: () => set({ error: null }),

    reset: () => set(initialState),
  }
})
